/**
=========================================================
* Trust Cargo - a container's status, as the office says it
=========================================================

* A container goes through four statuses, in this order:
*
*   planned -> loaded -> clearing -> arrived
*
* and that order is how a list of containers is read: what is still to come
* at the top, what has landed at the bottom. Alphabetical put "arrived" first
* and the order the server happened to send put nothing anywhere.
*
* The second one is stored as `loading` - it is a value of an ENUM column on a
* live table, and everything on the server that moves a container along
* matches on that word. The office calls it LOADED: a container with its
* number has loaded, it is not in the middle of loading. So the stored value
* stays what it is and every screen shows the office's word for it, from here,
* rather than each one spelling it its own way.
*/

/** The statuses in use, in the order a container goes through them. */
export const STATUS_ORDER = ["planned", "loading", "clearing", "arrived"];

/** What a stored status is called on screen, where that differs from how it is stored. */
const LABELS = { loading: "loaded" };

const key = (status) =>
  String(status ?? "")
    .trim()
    .toLowerCase();

/** The status as shown. Anything not renamed - or not known - is shown as it is. */
export function statusLabel(status) {
  const k = key(status);

  if (!k) return "";

  return LABELS[k] || String(status);
}

/** Where a status sits in the lifecycle; one that is not part of it goes last. */
export function statusRank(status) {
  const at = STATUS_ORDER.indexOf(key(status));

  return at === -1 ? STATUS_ORDER.length : at;
}

/**
 * Containers in lifecycle order. Within one status the rows keep the order
 * they came in - the sort is stable - so nothing else about the list changes.
 */
export function sortByStatus(rows) {
  return [...(rows || [])].sort((a, b) => statusRank(a?.status) - statusRank(b?.status));
}

/**
 * The choices for a status filter: the statuses these rows actually carry,
 * in lifecycle order and under the names the table shows. The value stays the
 * stored word, because that is what the rows are matched on.
 */
export function statusOptions(rows) {
  // exactly as the rows spell it - the filter compares the two as text
  const present = new Set();

  (rows || []).forEach((r) => {
    if (key(r?.status)) present.add(String(r.status));
  });

  return [...present]
    .sort((a, b) => statusRank(a) - statusRank(b) || a.localeCompare(b))
    .map((value) => ({ value, label: statusLabel(value) }));
}
